import { Box, Button, Paper, Step, StepLabel, Stepper, Typography } from '@mui/material';
import React, { useState } from 'react';
import type { MigrationJob } from '../../data/types';
import { date } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { AboutThisPage, PageHeader, StatusChip } from '../../ui/primitives';
import MigrationJobDetail from './MigrationJobDetail';
import NewMigrationDialog from './NewMigrationDialog';

const FLOW: MigrationJob['status'][] = ['UPLOADED', 'MAPPED', 'MATCHED', 'RECONCILED', 'COMMITTED'];
const NEXT: Partial<Record<MigrationJob['status'], string>> = {
  UPLOADED: 'Confirm column mapping',
  MAPPED: 'Run holder matching',
  MATCHED: 'Reconcile to source register',
  RECONCILED: 'Submit for approval to commit',
};

export default function MigrationPage() {
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(
    scope.migrations.find((job) => job.status !== 'COMMITTED')?.id ?? null,
  );
  const jobs = [...scope.migrations].sort((left, right) =>
    right.createdAt.localeCompare(left.createdAt),
  );
  return (
    <>
      <PageHeader
        title="Migration"
        subtitle="Move a fund's register from another provider: import, map, match holders, reconcile to the cent, then commit opening balances."
        actions={
          <Button variant="contained" onClick={() => setCreating(true)}>
            Import a register
          </Button>
        }
      />
      <AboutThisPage>
        <p>
          This is also how a new white-label client onboards. Imports accept the standard extracts
          from Registry Direct, Automic, Boardroom, MUFG and a generic spreadsheet. Holders are
          matched to existing contacts and entities by ABN, ACN, name and email, with a review queue
          for anything uncertain.
        </p>
        <p>
          Committing posts dated &quot;opening balance&quot; ledger entries and needs approval. Run
          one parallel distribution before switching the old registry off.
        </p>
      </AboutThisPage>
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {jobs.map((job) => {
          const step = FLOW.indexOf(job.status);
          const pendingApproval = state.approvals.some(
            (item) => item.refId === job.id && item.status === 'PENDING',
          );
          return (
            <Paper key={job.id} variant="outlined" sx={{ p: 2.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography variant="h6">
                    {scope.fundById.get(job.fundId)?.code} from {job.source}{' '}
                    <StatusChip status={pendingApproval ? 'PENDING_APPROVAL' : job.status} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {job.fileName} · {job.rows} holdings · uploaded {date(job.createdAt)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setExpanded(expanded === job.id ? null : job.id)}
                  >
                    {expanded === job.id ? 'Hide detail' : 'Detail'}
                  </Button>
                  {NEXT[job.status] && !pendingApproval && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        dispatch({ type: 'MIGRATION_ADVANCE', id: job.id });
                        toast(
                          job.status === 'RECONCILED'
                            ? 'Sent to Approvals'
                            : `${NEXT[job.status]}: complete`,
                        );
                      }}
                    >
                      {NEXT[job.status]}
                    </Button>
                  )}
                </Box>
              </Box>
              <Stepper
                activeStep={job.status === 'COMMITTED' ? FLOW.length : step + 1}
                sx={{ mt: 2 }}
                alternativeLabel
              >
                {['Uploaded', 'Mapped', 'Matched', 'Reconciled', 'Committed'].map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              {expanded === job.id && <MigrationJobDetail job={job} />}
            </Paper>
          );
        })}
      </Box>
      <NewMigrationDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}
